<template>
  <div class="profile-dashboard container mt-4">
    <div class="row mb-5 align-items-center">
      <div class="col">
        <h2 class="font-weight-bold" style="color: #2c3e50;">
          <span v-if="isAdmin" class="badge badge-danger mr-2" style="font-size:0.7rem;vertical-align:middle;">ADMIN</span>
          {{ isAdmin ? 'All Dashboards' : 'My Dashboards' }}
        </h2>
        <p class="text-muted mb-0">{{ isAdmin ? 'Viewing and managing all dashboards across all users.' : 'Manage and view the customized dashboards you have created.' }}</p>
      </div>
      <div class="col-auto">
        <b-button variant="primary" style="background: #007D53; border: none;" @click="startFresh">
          <b-icon icon="plus" class="mr-1"></b-icon> Create New Dashboard
        </b-button>
      </div>
    </div>

    <!-- Dashboards Grid -->
    <div class="row" v-if="instances && instances.length">
      <div v-for="instance in instances" :key="instance.id" class="col-md-6 col-lg-4 mb-4">
        <div class="card h-100 dashboard-card shadow-sm">
          <div class="card-body">
            <h5 class="card-title text-truncate" :title="instance.config?.name || 'Untitled Dashboard'">
              {{ instance.config?.name || 'Untitled Dashboard' }}
            </h5>
            <p class="card-text text-muted small description-text">
              {{ instance.config?.description || 'No description provided.' }}
            </p>
            <div class="mt-3 text-muted" style="font-size: 0.8rem;">
              <b-icon icon="clock"></b-icon> Created: {{ formatDate(instance.createdAt) }}
            </div>
            <div v-if="isAdmin" class="mt-1 text-muted" style="font-size: 0.75rem;">
              <b-icon icon="person"></b-icon> Owner: <code>{{ instance.ownerId || instance.userId || instance.user || 'unknown' }}</code>
            </div>
          </div>
          <div class="card-footer bg-white border-top-0 d-flex justify-content-between p-3">
            <b-button size="sm" variant="outline-secondary" class="mr-2 flex-grow-1" @click="loadConfiguration(instance.id)">
              <b-icon icon="pencil-square"></b-icon> Edit
            </b-button>
            <b-button size="sm" variant="primary" style="background: #007D53; border: none;" class="flex-grow-1 mr-2" @click="viewDashboard(instance.id)">
              <b-icon icon="eye"></b-icon> View
            </b-button>
            <b-button size="sm" variant="outline-danger" @click="confirmDelete(instance.id, instance.config?.name)">
              <b-icon icon="trash"></b-icon>
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5 empty-state shadow-sm rounded bg-white">
      <b-icon icon="inbox" font-scale="4" class="text-secondary mb-3"></b-icon>
      <h4 class="font-weight-bold">No Dashboards Found</h4>
      <p class="text-muted mb-4">You haven't created any custom dashboards yet.</p>
      <b-button variant="outline-primary" @click="startFresh">Get Started</b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'ConfigurerProfile',
  computed: {
    ...mapState('configurer', ['instances']),
    ...mapState('configurerAuth', ['isAdmin']),
  },
  mounted() {
    this.fetchInstances();
  },
  methods: {
    ...mapActions('configurer', ['fetchInstances', 'loadInstance', 'resetConfigurer', 'deleteInstance']),
    startFresh() {
      this.resetConfigurer();
      this.$router.push('/configure/user/customize');
    },
    formatDate(isoString) {
      if (!isoString) return 'Unknown';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(isoString).toLocaleDateString(undefined, options);
    },
    async loadConfiguration(id) {
      await this.loadInstance(id);
      this.$router.push('/configure/user/customize');
    },
    viewDashboard(id) {
      this.$router.push(`/generated-dashboard/${id}`);
    },
    async confirmDelete(id, name) {
      const result = await this.$swal({
        icon: 'warning',
        title: 'Delete Dashboard?',
        html: `<p>You are about to permanently delete <strong>${name || 'this dashboard'}</strong> and all its data from Firebase.</p><p class="text-muted small">This action cannot be undone.</p>`,
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete It',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc3545',
      });
      if (result.isConfirmed) {
        try {
          await this.deleteInstance(id);
          this.$bvToast.toast('Dashboard deleted successfully.', {
            title: 'Deleted',
            variant: 'success',
            solid: true,
          });
        } catch (e) {
          this.$bvToast.toast('Failed to delete the dashboard. Please try again.', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
  },
};
</script>

<style scoped>
.dashboard-card {
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.dashboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
}

.description-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  border: 1px dashed rgba(0,0,0,0.1);
  padding: 60px 20px;
}
</style>
